import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRaces, useCreateRace, useUpdateRace, useDeleteRace, Race, CreateRaceInput, UpdateRaceInput } from './hooks';
import RaceModal from './RaceModal';
import UpcomingTable from './UpcomingTable';
import PastRacesSection from './PastRacesSection';
import MenuButton from '../../components/MenuButton';
import Snackbar from '../../components/Snackbar';

interface ModalState {
    open: boolean;
    editRace?: Race;
}

const RacesPage: React.FC = () => {
    const { data: races = [], isLoading } = useRaces();
    const createRace = useCreateRace();
    const updateRace = useUpdateRace();
    const deleteRace = useDeleteRace();
    const { t } = useTranslation();

    const [modal, setModal] = useState<ModalState>({ open: false });
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [snackbar, setSnackbar] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

    const upcoming = races.filter((r) => r.status !== 'completed' && r.status !== 'cancelled');
    const past = races.filter((r) => r.status === 'completed');

    const handleOpenAdd = () => setModal({ open: true });
    const handleOpenEdit = (race: Race) => setModal({ open: true, editRace: race });
    const handleClose = () => setModal({ open: false });

    const handleSubmit = async (data: CreateRaceInput | UpdateRaceInput) => {
        if (modal.editRace) {
            await updateRace.mutateAsync({ id: modal.editRace.id, input: data as UpdateRaceInput });
            setSnackbar({ message: t('races.updatedSuccess'), type: 'success' });
        } else {
            await createRace.mutateAsync(data as CreateRaceInput);
            setSnackbar({ message: t('races.createdSuccess'), type: 'success' });
        }
    };

    const handleDelete = async (id: string) => {
        setDeletingId(id);
        try {
            await deleteRace.mutateAsync(id);
            setSnackbar({ message: t('races.deletedSuccess'), type: 'success' });
        } catch {
            setSnackbar({ message: t('races.deleteError'), type: 'error' });
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div className="min-h-screen bg-background">

            {/* Header */}
            <header className="h-24 bg-gradient-to-r from-cyan-500 to-blue-500 border-b border-cyan-600 flex items-center justify-between px-4 md:px-8 shadow-md sticky top-0 z-20 relative overflow-hidden">
                <div className="absolute -right-20 -top-20 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>

                <div className="relative z-10 flex items-center gap-3 md:gap-6 w-full justify-between">
                    <div className="flex items-center gap-3 md:gap-4 min-w-0">
                        <MenuButton />
                        <div className="hidden sm:flex w-10 h-10 rounded-full bg-white/10 items-center justify-center text-white border border-white/20 flex-shrink-0">
                            <i className="fa-solid fa-flag-checkered text-lg"></i>
                        </div>
                        <div className="min-w-0">
                            <h1 className="text-base md:text-xl font-bold text-white tracking-tight truncate">{t('races.title')}</h1>
                            <p className="hidden md:block text-cyan-100 text-xs font-medium">{t('races.subtitle')}</p>
                        </div>
                    </div>

                    <button
                        onClick={handleOpenAdd}
                        className="flex items-center gap-2 px-6 py-2.5 bg-accent hover:bg-lime-500 text-white rounded font-bold hover:shadow-lg transition-all shadow-md shadow-lime-900/20 border-b-4 border-lime-600 active:border-b-0 active:translate-y-1"
                    >
                        <i className="fa-solid fa-plus text-lg"></i>
                        <span>{t('races.addNew')}</span>
                    </button>
                </div>
            </header>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 lg:p-8">
                <div className="max-w-7xl mx-auto flex flex-col gap-8 pb-20">

                    {/* Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-blue-50 text-blue-500 flex items-center justify-center">
                                <i className="fa-solid fa-calendar-check text-xl"></i>
                            </div>
                            <div>
                                <p className="text-xs font-bold text-slate-400 uppercase tracking-wide">{t('races.statsUpcoming')}</p>
                                <h3 className="text-2xl font-bold text-slate-800">{upcoming.length} {t('races.races')}</h3>
                            </div>
                        </div>
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-green-50 text-green-500 flex items-center justify-center">
                                <i className="fa-solid fa-check-double text-xl"></i>
                            </div>
                            <div>
                                <p className="text-xs font-bold text-slate-400 uppercase tracking-wide">{t('races.statsCompleted')}</p>
                                <h3 className="text-2xl font-bold text-slate-800">{past.length} {t('races.races')}</h3>
                            </div>
                        </div>
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-yellow-50 text-yellow-500 flex items-center justify-center">
                                <i className="fa-solid fa-medal text-xl"></i>
                            </div>
                            <div>
                                <p className="text-xs font-bold text-slate-400 uppercase tracking-wide">{t('races.statsPB')}</p>
                                <h3 className="text-2xl font-bold text-slate-800">
                                    {past.filter((r) => r.achievement?.toLowerCase().includes('pb')).length} PB
                                </h3>
                            </div>
                        </div>
                    </div>

                    {/* Upcoming Races Table */}
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                        <div className="px-6 py-4 bg-gradient-to-r from-cyan-100 to-cyan-50 border-b border-cyan-200 flex items-center justify-between">
                            <div>
                                <h3 className="font-bold text-slate-800 text-lg flex items-center gap-2">
                                    <i className="fa-solid fa-person-running text-cyan-600"></i> {t('races.upcomingTitle')}
                                </h3>
                                <p className="text-xs text-cyan-800 mt-1">{t('races.upcomingDesc')}</p>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            {isLoading ? (
                                <div className="py-12 text-center text-slate-400">
                                    <i className="fa-solid fa-spinner fa-spin text-2xl"></i>
                                </div>
                            ) : (
                                <table className="w-full text-sm text-left">
                                    <thead className="bg-cyan-50 text-cyan-700 uppercase font-bold text-xs border-b border-cyan-200">
                                        <tr>
                                            <th className="px-6 py-4 w-16 text-center">#</th>
                                            <th className="px-6 py-4">{t('races.colType')}</th>
                                            <th className="px-6 py-4">{t('races.colDate')}</th>
                                            <th className="px-6 py-4">{t('races.colLocation')}</th>
                                            <th className="px-6 py-4">{t('races.colNotes')}</th>
                                            <th className="px-6 py-4 text-center">{t('races.colStatus')}</th>
                                            <th className="px-6 py-4 text-right">{t('races.colActions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-cyan-100">
                                        <UpcomingTable
                                            races={upcoming}
                                            deletingId={deletingId}
                                            onEdit={handleOpenEdit}
                                            onDelete={handleDelete}
                                        />
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>

                    {/* Past Races */}
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                        <div className="px-6 py-4 bg-gradient-to-r from-cyan-100 to-cyan-50 border-b border-cyan-200">
                            <h3 className="font-bold text-slate-800 text-lg flex items-center gap-2">
                                <i className="fa-solid fa-history text-cyan-600"></i> {t('races.pastTitle')}
                            </h3>
                            <p className="text-xs text-cyan-800 mt-1">{t('races.pastDesc')}</p>
                        </div>
                        <div className="p-6">
                            {isLoading ? (
                                <div className="py-8 text-center text-slate-400">
                                    <i className="fa-solid fa-spinner fa-spin text-2xl"></i>
                                </div>
                            ) : (
                                <PastRacesSection
                                    races={past}
                                    deletingId={deletingId}
                                    onEdit={handleOpenEdit}
                                    onDelete={handleDelete}
                                />
                            )}
                        </div>
                    </div>

                </div>
            </div>

            {/* Modal */}
            <RaceModal
                isOpen={modal.open}
                initialValues={modal.editRace}
                onClose={handleClose}
                onSubmit={handleSubmit}
            />

            {/* Snackbar */}
            {snackbar && (
                <Snackbar
                    message={snackbar.message}
                    type={snackbar.type}
                    onClose={() => setSnackbar(null)}
                />
            )}
        </div>
    );
};

export default RacesPage;
